import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "../components/Reveal";
import MediaGrid from "../components/MediaGrid";
import Showroom from "../components/Showroom";
import TradeSection from "../components/TradeSection";
import {
  brand,
  collections,
  features,
  social,
  certifications,
} from "../data/site";
import gallery from "../data/gallery";

// Rotating banner — all three images feature people wearing the jewellery.
const bannerSlides = [
  {
    image: "/assets/cd/model-black.jpg",
    position: "object-[center_20%]",
    caption: "Diamond necklaces, worn beautifully",
  },
  {
    image: "/assets/cd/model-emerald.jpg",
    position: "object-[center_25%]",
    caption: "Diamonds paired with colour",
  },
  {
    image: "/assets/cd/model-hand.jpg",
    position: "object-[center_30%]",
    caption: "Everyday brilliance in diamond & CZ",
  },
];

// A curated flat selection for the home gallery preview.
const previewPosts = [
  ...gallery[0].posts.slice(0, 4),
  ...gallery[1].posts.slice(0, 2),
  ...gallery[2].posts.slice(0, 2),
];

function HeroSlider() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setActive((i) => (i + 1) % bannerSlides.length),
      5000
    );
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative flex min-h-screen items-center justify-center overflow-hidden bg-charcoal">
      {bannerSlides.map((slide, i) => (
        <img
          key={slide.image}
          src={slide.image}
          alt={slide.caption}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${slide.position} ${
            i === active ? "opacity-100 animate-slowZoom" : "opacity-0"
          }`}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-charcoal/60 via-charcoal/35 to-charcoal/55" />

      <div className="container-luxe relative z-10 text-center text-cream">
        <p className="mb-6 animate-fadeUp font-sans text-xs uppercase tracking-luxe text-gold-light">
          {brand.legalName}
        </p>
        <h1 className="mx-auto max-w-5xl font-serif text-4xl font-light leading-[1.08] sm:text-5xl md:text-7xl lg:text-8xl">
          Diamonds As Unique
          <span className="block italic text-gold-light">As You</span>
        </h1>
        <p className="mx-auto mt-8 max-w-xl font-sans text-sm font-light leading-relaxed tracking-wide text-cream/85 md:text-base">
          Certified natural &amp; lab-grown diamonds and premium CZ jewellery —
          GIA, IGI &amp; SGI graded for uncompromised brilliance.
        </p>
        <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <Link to="/collections" className="btn-ghost">
            Explore Collections
          </Link>
          <Link
            to="/diamonds"
            className="font-sans text-xs uppercase tracking-[0.25em] text-cream/85 underline-offset-8 transition-colors hover:text-gold-light hover:underline"
          >
            Learn About Diamonds
          </Link>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-10 left-1/2 z-10 flex -translate-x-1/2 gap-3">
        {bannerSlides.map((s, i) => (
          <button
            key={s.image}
            aria-label={`Show slide ${i + 1}`}
            onClick={() => setActive(i)}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              i === active ? "w-8 bg-gold" : "w-4 bg-cream/40 hover:bg-cream/70"
            }`}
          />
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      {/* ===== Hero (rotating banner) ===== */}
      <HeroSlider />

      {/* ===== Legacy ===== */}
      <section className="bg-white py-16 md:py-32">
        <div className="container-luxe grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal className="relative">
            <div className="overflow-hidden">
              <img
                src="/assets/about/directors.jpg"
                alt="The directors of Culture Diamond Jewels"
                className="h-[420px] w-full object-cover object-top sm:h-[500px] lg:h-[560px]"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 hidden h-32 w-32 border border-gold/40 lg:block" />
          </Reveal>

          <Reveal delay={150}>
            <p className="eyebrow mb-5">The Legacy</p>
            <h2 className="section-title mb-8">
              A house built on <span className="italic text-gold-dark">trust</span>
            </h2>
            <div className="space-y-5 font-sans text-base font-light leading-relaxed text-charcoal/70">
              <p>
                Culture Diamond Jewels is led by directors Kevinbhai, Bhadreshbhai
                and Chandrakantbhai — a house devoted to fine diamonds and premium
                craftsmanship, based at Ten-11 on C.G. Road, Ahmedabad.
              </p>
              <p>
                From natural and lab-grown diamonds to premium CZ, every stone is
                hand-selected and independently certified by GIA, IGI or SGI. Our
                promise is simple: diamonds as unique as you.
              </p>
            </div>
            <Link to="/about" className="btn-gold mt-10">
              Our Story
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ===== Features ===== */}
      <section className="border-y border-gold/15 bg-cream py-20">
        <div className="container-luxe grid gap-12 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f, i) => (
            <Reveal key={f.title} delay={i * 100}>
              <div className="hairline mb-6" />
              <h3 className="mb-3 font-serif text-2xl font-light text-charcoal">{f.title}</h3>
              <p className="font-sans text-sm font-light leading-relaxed text-charcoal/60">
                {f.text}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ===== Collections ===== */}
      <section className="bg-white py-16 md:py-32">
        <div className="container-luxe">
          <Reveal className="mb-16 text-center">
            <p className="eyebrow mb-5">The Collections</p>
            <h2 className="section-title">Diamonds &amp; premium CZ</h2>
          </Reveal>

          <div className="grid gap-6 md:grid-cols-3">
            {collections.map((c, i) => (
              <Reveal key={c.id} delay={i * 120}>
                <Link to="/collections" className="group block">
                  <div className="relative overflow-hidden">
                    <img
                      src={c.image}
                      alt={c.name}
                      className="h-[360px] w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110 sm:h-[440px]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal/85 via-charcoal/10 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-7 text-cream">
                      <h3 className="font-serif text-3xl font-light">{c.name}</h3>
                      <p className="mt-2 max-w-xs font-sans text-sm font-light leading-relaxed text-cream/80">
                        {c.blurb}
                      </p>
                      <span className="mt-4 inline-block font-sans text-xs uppercase tracking-[0.25em] text-gold-light opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                        Discover →
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 4Cs / Certification teaser ===== */}
      <section className="border-y border-gold/15 bg-charcoal py-20 md:py-28">
        <div className="container-luxe">
          <Reveal className="mb-14 text-center">
            <p className="eyebrow mb-5 text-gold-light">Diamond Quality</p>
            <h2 className="section-title text-cream">Graded on the 4Cs</h2>
            <p className="mx-auto mt-5 max-w-xl font-sans text-sm font-light leading-relaxed text-cream/70">
              Our diamonds are chosen for excellent cut, E/F colour and VVS
              clarity — and certified by the world's leading laboratories.
            </p>
          </Reveal>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { c: "Cut", t: "Excellent cut for maximum fire and brilliance." },
              { c: "Colour", t: "E/F near-colourless grades for a pure, icy white." },
              { c: "Clarity", t: "VVS clarity — eye-clean with minimal inclusions." },
              { c: "Carat", t: "A spectrum of carat weights to suit every piece." },
            ].map((x, i) => (
              <Reveal key={x.c} delay={i * 100}>
                <div className="border-t border-gold/30 pt-5">
                  <h3 className="font-serif text-2xl font-light text-gold-light">{x.c}</h3>
                  <p className="mt-2 font-sans text-sm font-light leading-relaxed text-cream/60">
                    {x.t}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-12 flex flex-col items-center gap-6">
            <div className="flex flex-wrap items-center justify-center gap-4">
              {certifications.map((cert) => (
                <span
                  key={cert.code}
                  className="border border-gold/40 px-5 py-2 font-sans text-xs uppercase tracking-[0.25em] text-gold-light"
                >
                  {cert.code} Certified
                </span>
              ))}
            </div>
            <Link to="/diamonds" className="btn-ghost">
              The Diamond Guide
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ===== Import & Export ===== */}
      <TradeSection />

      {/* ===== Showroom ===== */}
      <Showroom />

      {/* ===== Digital Atelier ===== */}
      <section className="border-t border-gold/15 bg-cream py-16 md:py-32">
        <div className="container-luxe">
          <Reveal className="mb-14 flex flex-col items-center text-center">
            <p className="eyebrow mb-5">Follow Us</p>
            <h2 className="section-title mb-5">Follow the brilliance</h2>
            <a
              href={social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="font-sans text-sm tracking-wide text-charcoal/70 transition-colors hover:text-gold-dark"
            >
              {social.instagramHandle}
            </a>
          </Reveal>

          <Reveal>
            <MediaGrid items={previewPosts} />
          </Reveal>

          <div className="mt-12 text-center">
            <Link to="/gallery" className="btn-gold">
              View the Full Gallery
            </Link>
          </div>
        </div>
      </section>

      {/* ===== Contact CTA ===== */}
      <CtaBand />
    </>
  );
}

function CtaBand() {
  return (
    <section className="relative overflow-hidden py-28">
      <img
        src="/assets/cd/model-hand.jpg"
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-[center_30%]"
      />
      <div className="absolute inset-0 bg-charcoal/80" />
      <div className="container-luxe relative z-10 text-center text-cream">
        <p className="mb-5 font-sans text-xs uppercase tracking-luxe text-gold-light">
          Visit Us
        </p>
        <Reveal>
          <h2 className="mx-auto max-w-3xl font-serif text-4xl font-light leading-tight text-cream md:text-5xl lg:text-6xl">
            Find your perfect diamond
          </h2>
          <p className="mx-auto mt-6 max-w-xl font-sans text-base font-light leading-relaxed text-cream/80">
            Visit our Ten-11 showroom on C.G. Road, Ahmedabad, or reach out to
            explore diamonds and premium CZ chosen just for you.
          </p>
          <Link to="/contact" className="btn-ghost mt-10">
            Get in Touch
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
